/**
 * Markdown — renders the .txt content Anna writes in the Drive folder.
 *
 * GFM is enabled so she can use tables and task lists. `remark-breaks` is
 * enabled because legacy content was written as plain text with single
 * newlines for paragraph breaks; without this every linebreak would
 * collapse and the page would read as one long blob.
 *
 * `rehype-raw` is enabled so the CMS can embed trusted HTML snippets
 * (YouTube iframes, <audio> tags for interview clips) directly inside
 * the .txt — the source is the Drive folder which only Anna writes to,
 * so there's no XSS surface.
 *
 * `components.a` custom override: when Anna writes a plain markdown
 * link to a YouTube video (`[watch](https://youtu.be/…)`), we auto-
 * upgrade it to an embedded iframe in the same `.embed-video` container
 * the manually-authored iframes already use. That way she never needs
 * to remember the raw <iframe> tag — just paste the video URL.
 */

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeRaw from 'rehype-raw'
import type { AnchorHTMLAttributes, ReactNode } from 'react'

interface MarkdownProps {
  children: string | null | undefined
  className?: string
}

/**
 * Extract the 11-character YouTube video ID from any of the URL shapes
 * Anna is likely to paste:
 *   - https://youtu.be/<id>?si=…
 *   - https://www.youtube.com/watch?v=<id>&…
 *   - https://www.youtube.com/embed/<id>
 *   - https://youtube.com/shorts/<id>
 * Returns null for non-YouTube URLs so the caller can fall through to a
 * normal anchor.
 */
function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url)
    const host = u.hostname.replace(/^www\./, '')
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1)
      return id || null
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v')
      if (v) return v
      const m = u.pathname.match(/^\/(?:embed|shorts)\/([^\/?#&]+)/)
      if (m) return m[1]
    }
    return null
  } catch {
    return null
  }
}

function AnchorOrEmbed({
  href,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { children?: ReactNode }) {
  const videoId = href ? extractYouTubeId(href) : null
  if (videoId) {
    const title =
      typeof children === 'string' && children.trim()
        ? children.trim()
        : 'Video ansehen'
    return (
      <span className="embed-video">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </span>
    )
  }
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  )
}

export function Markdown({ children, className }: MarkdownProps) {
  if (!children) return null
  return (
    <div className={`markdown-prose ${className || ''}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw]}
        components={{ a: AnchorOrEmbed }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
